import { DynamoDBClient, UpdateItemCommand, UpdateItemInput } from '@aws-sdk/client-dynamodb';
import { CheckIcon, CogIcon, LogoutIcon, SelectorIcon, XIcon } from '@heroicons/react/solid';
import { Fragment, useEffect, useState } from 'react';
import { User } from '../../models/User';
import { Menu, Listbox, Transition } from '@headlessui/react';
import { states } from './States';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface HeaderProps {
  user: User;
  dynamoDbClient: DynamoDBClient;
  signOut: () => void;
  onSettingsSaved: () => void;
  onShowAlert: (status: 'SUCCESS' | 'ERROR', message: string, subMessages?: string[]) => void;
}

export const Header = (props: HeaderProps) => {
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [userCopy, setUserCopy] = useState<User>();
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
  });
  const handleResize = () => {
    setDimensions({
      width: window.innerWidth,
    });
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize, false);
    return () => {
      window.removeEventListener('resize', handleResize, false);
    };
  }, []);

  useEffect(() => {
    if (!isSettingsModalOpen) {
      setUserCopy({ ...props.user }); // Reset userCopy only when closed to prevent re-rendering during the opening animation
    }
  }, [props.user, isSettingsModalOpen]); // Removed alertSubMessages from here

  useEffect(() => {
    if (isSettingsModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSettingsModalOpen]);

  const validation = (): boolean => {
    if (userCopy) {
      const errors: string[] = [];
      baseStringValidator('First name', userCopy.firstName, errors);
      if (!/^[A-Za-z\-\s]+$/.test(userCopy.lastName)) {
        errors.push(`Last name must contain only letters, hyphens, and spaces`);
      }
      birthdayValidator(userCopy.birthday, errors); // Moved outside of advisor check
      if (!userCopy.advisor) { // Only validate these if not an advisor
        baseStringValidator('City', userCopy.city, errors); // Moved inside non-advisor check
        phoneValidator(userCopy.cellNumber, errors); // Moved inside non-advisor check
        zipCodeValidator(userCopy.zipCode, errors); // Moved inside non-advisor check
      }
      if (errors.length > 0) {
        props.onShowAlert('ERROR', 'Please fix the following validation errors:', errors);
        return false;
      }
    }
    return true;
  };

  const birthdayValidator = (birthday: string, errors: string[]) => {
    if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}/.test(birthday)) {
      errors.push(`Birthday must be of the form YYYY-MM-DD`);
    }
  };

  const zipCodeValidator = (zipCode: string, errors: string[]) => {
    if (!/^[0-9]{5}/.test(zipCode)) {
      errors.push(`Zip code must be a 5 digit number`);
    }
  };

  const phoneValidator = (phoneNumber: string, errors: string[]) => {
    if (!(/^[0-9]{3}-[0-9]{3}-[0-9]{4}/.test(phoneNumber) || /^[0-9]{10}/.test(phoneNumber))) {
      errors.push(`Cell number must be of the form 999-999-9999 or 2039991234`);
    }
  };

  const baseStringValidator = (fieldName: string, field: string, errors: string[]) => {
    if (!/^[A-Za-z\s]+$/.test(field)) {
      errors.push(`${fieldName} must contain only letters and spaces`);
    }
  };

  const logout = () => {
    props.signOut();
  };

  const submit = async () => {
    if (userCopy && validation()) {
      const input: UpdateItemInput = {
        TableName: 'MitchellFC',
        Key: { cognitoUserId: { S: userCopy.cognitoUserId } },
        UpdateExpression:
          'set firstName = :firstName, lastName = :lastName, cellNumber = :cellNumber, birthday = :birthday, streetAddress = :streetAddress, city = :city, stateText = :stateText, zipCode = :zipCode, darkMode = :darkMode',
        ExpressionAttributeValues: {
          ':firstName': { S: userCopy.firstName },
          ':lastName': { S: userCopy.lastName },
          ':cellNumber': { S: userCopy.cellNumber },
          ':birthday': { S: userCopy.birthday },
          ':streetAddress': { S: userCopy.streetAddress },
          ':city': { S: userCopy.city },
          ':stateText': { S: userCopy.stateText },
          ':zipCode': { S: userCopy.zipCode },
          ':darkMode': { BOOL: userCopy.darkMode },
        },
      };
      props.dynamoDbClient
        .send(new UpdateItemCommand(input))
        .then(() => {
          localStorage.setItem('darkMode', userCopy.darkMode.toString());
          props.onSettingsSaved(); // Call the refresh function from SinglePage
          setIsSettingsModalOpen(false); // Close modal on success
          props.onShowAlert('SUCCESS', 'Settings saved successfully!');
        })
        .catch(() => {
          props.onShowAlert('ERROR', 'Settings not saved.');
        });
    }
  };

  // Helper function to scroll to a section
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      // Get the header height dynamically for offset
      const headerElement = document.querySelector('header'); // Assuming the header is the first <header> tag
      const headerOffset = headerElement ? headerElement.offsetHeight : 0;
      window.scrollTo({ top: element.offsetTop - headerOffset, behavior: 'smooth' });
    }
  };

  const isMobile = dimensions.width < 768;

  return (
    <>
      <style>
        {`
          .react-datepicker-popper {
            z-index: 1000 !important;
            padding-top: 8px !important;
          }
          .react-datepicker__triangle {
            display: none !important;
          }
          .react-datepicker {
            font-family: inherit !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 1rem !important;
            box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
          }
          .dark .react-datepicker {
            background-color: #1e293b !important;
            border-color: #334155 !important;
          }
          .react-datepicker__header {
            background-color: #f8fafc !important;
            border-bottom: 1px solid #e2e8f0 !important;
            border-top-left-radius: 1rem !important;
            border-top-right-radius: 1rem !important;
            padding-top: 1rem !important;
          }
          .dark .react-datepicker__header {
            background-color: #0f172a !important;
            border-bottom-color: #334155 !important;
          }
          .react-datepicker__current-month, .react-datepicker__year-read-view--selected-year, .react-datepicker__month-read-view--selected-month {
            color: #0f172a !important;
            font-weight: 700 !important;
            font-size: 1rem !important;
          }
          .dark .react-datepicker__current-month, .dark .react-datepicker__year-read-view--selected-year, .dark .react-datepicker__month-read-view--selected-month {
            color: #f8fafc !important;
          }
          .react-datepicker__day-name, .react-datepicker__day {
            color: #475569 !important;
            margin: 0.2rem !important;
            width: 2rem !important;
            line-height: 2rem !important;
            border-radius: 0.5rem !important;
          }
          .dark .react-datepicker__day-name, .dark .react-datepicker__day {
            color: #cbd5e1 !important;
          }
          .react-datepicker__day:hover {
            background-color: #f1f5f9 !important;
          }
          .dark .react-datepicker__day:hover {
            background-color: #334155 !important;
          }
          .react-datepicker__day--selected {
            background-color: #2563eb !important;
            color: white !important;
          }
          .dark .react-datepicker__day--selected {
            background-color: #3b82f6 !important;
          }
          .react-datepicker__day--keyboard-selected {
            background-color: #bfdbfe !important;
            color: #1e3a8a !important;
          }
          .dark .react-datepicker__day--keyboard-selected {
            background-color: #1e3a8a !important;
            color: #bfdbfe !important;
          }
          .react-datepicker__day--outside-month {
            color: #cbd5e1 !important;
          }
          .dark .react-datepicker__day--outside-month {
            color: #475569 !important;
          }
          .react-datepicker__year-read-view,
          .react-datepicker__month-read-view {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 0.25rem !important;
            flex-direction: row-reverse !important;
          }
          .react-datepicker__year-read-view--down-arrow,
          .react-datepicker__month-read-view--down-arrow {
            position: relative !important;
            top: auto !important;
            right: auto !important;
            margin-bottom: 2px !important;
            border-color: #64748b !important;
          }
          .dark .react-datepicker__year-read-view--down-arrow,
          .dark .react-datepicker__month-read-view--down-arrow {
            border-color: #94a3b8 !important;
          }
          .react-datepicker__year-dropdown,
          .react-datepicker__month-dropdown {
            background-color: #ffffff !important;
            border: 1px solid #e2e8f0 !important;
            border-radius: 0.5rem !important;
            box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1) !important;
            scrollbar-width: thin;
          }
          .dark .react-datepicker__year-dropdown,
          .dark .react-datepicker__month-dropdown {
            background-color: #1e293b !important;
            border-color: #334155 !important;
          }
          .react-datepicker__year-option,
          .react-datepicker__month-option {
            padding: 0.5rem !important;
            color: #0f172a !important;
          }
          .dark .react-datepicker__year-option,
          .dark .react-datepicker__month-option {
            color: #f8fafc !important;
          }
          .react-datepicker__year-option:hover,
          .react-datepicker__month-option:hover {
            background-color: #f1f5f9 !important;
          }
          .dark .react-datepicker__year-option:hover,
          .dark .react-datepicker__month-option:hover {
            background-color: #334155 !important;
          }
          .react-datepicker__navigation-icon::before {
            border-color: #64748b !important;
          }
          .dark .react-datepicker__navigation-icon::before {
            border-color: #94a3b8 !important;
          }
          .react-datepicker__portal {
            background-color: rgba(15, 23, 42, 0.7) !important;
            backdrop-filter: blur(4px) !important;
            z-index: 1000 !important;
          }
        `}
      </style>
      <motion.header 
        initial={{ y: isMobile ? 0 : -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="dark:bg-slate-900/80 bg-white/70 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 pt-4 pb-4 flex items-center w-full sticky top-0 z-50 transition-colors duration-500"
      >
        <div className="container mx-auto px-6 flex items-center justify-between">
          <div className="uppercase text-slate-900 dark:text-white font-black text-xl lg:text-2xl tracking-tighter mr-4">Mitchell Family Council</div>
          <div className="flex items-center">
            <nav className="font-sen text-slate-800 text-lg items-center lg:hidden">
              <Menu as="div" className="relative inline-block text-left">
                <div>
                  <Menu.Button className="flex items-center justify-center rounded-xl p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 w-56 mt-2 origin-top-right bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700 rounded-2xl shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                    <div className="p-2 space-y-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button // Changed from HashLink
                            onClick={() => scrollToSection('home')} // Added onClick
                            className={`cursor-pointer group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors ${
                              active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            Home
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button // Changed from HashLink
                            onClick={() => scrollToSection('documentDisplayer')} // Added onClick
                            className={`cursor-pointer group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors ${
                              active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            Document
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button // Changed from HashLink
                            onClick={() => scrollToSection('contacts')} // Added onClick
                            className={`cursor-pointer group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors ${
                              active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            Contacts
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button // Changed from HashLink
                            onClick={() => scrollToSection('photos')} // Added onClick
                            className={`cursor-pointer group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors ${
                              active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            Photos
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button // Changed from HashLink
                            onClick={() => scrollToSection('videos')} // Added onClick
                            className={`cursor-pointer group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors ${
                              active ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            Videos
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                    <div className="p-2 space-y-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => setIsSettingsModalOpen(true)}
                            className={`cursor-pointer group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors ${
                              active ? 'bg-slate-50 dark:bg-slate-700/50 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            <CogIcon className={`w-5 h-5 mr-3 transition-colors ${active ? 'text-blue-500' : 'text-slate-400'}`} aria-hidden="true" />
                            Settings
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={logout}
                            className={`cursor-pointer group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors ${
                              active ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                            }`}
                          >
                            <LogoutIcon className={`w-5 h-5 mr-3 transition-colors ${active ? 'text-red-500' : 'text-slate-400'}`} aria-hidden="true" />
                            Logout
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </nav>
            <nav className="font-sen text-slate-600 dark:text-slate-300 uppercase text-sm font-bold lg:flex items-center hidden tracking-wide">
              <button // Changed from HashLink
                onClick={() => scrollToSection('home')} // Added onClick
                className="text-sm cursor-pointer py-2 px-4 flex hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                HOME
              </button>
              <button // Changed from HashLink
                onClick={() => scrollToSection('documentDisplayer')} // Added onClick
                className="text-sm cursor-pointer py-2 px-4 flex hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                DOCUMENTS
              </button>
              <button // Changed from HashLink
                onClick={() => scrollToSection('contacts')} // Added onClick
                className="text-sm cursor-pointer py-2 px-4 flex hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                CONTACTS
              </button>
              <button // Changed from HashLink
                onClick={() => scrollToSection('photos')} // Added onClick
                className="text-sm cursor-pointer py-2 px-4 flex hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                PHOTOS
              </button>
              <button // Changed from HashLink
                onClick={() => scrollToSection('videos')} // Added onClick
                className="text-sm cursor-pointer py-2 px-4 flex hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                VIDEOS
              </button>
              <div className="ml-4 text-right">
                <Menu as="div" className="relative inline-block text-left">
                  <div>
                    <Menu.Button className="flex items-center justify-center rounded-full h-10 w-10 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400 font-bold hover:ring-2 hover:ring-blue-500 hover:ring-offset-2 dark:hover:ring-offset-slate-900 transition-all focus:outline-none shadow-sm">
                      {props.user?.firstName?.charAt(0)}{props.user?.lastName?.charAt(0)}
                    </Menu.Button>
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 w-64 mt-3 origin-top-right bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700 rounded-2xl shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="px-4 py-3">
                        <p className="text-sm text-gray-500 dark:text-slate-400">Signed in as</p>
                        <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{props.user?.firstName} {props.user?.lastName}</p>
                      </div>
                      <div className="p-2 space-y-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? 'bg-slate-50 dark:bg-slate-700/50 text-blue-600 dark:text-blue-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                              } group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors`}
                              onClick={() => setIsSettingsModalOpen(true)}
                            >
                              <CogIcon className={`w-5 h-5 mr-3 transition-colors ${active ? 'text-blue-500' : 'text-slate-400'}`} aria-hidden="true" />
                              Settings
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                              } group flex rounded-xl items-center w-full px-3 py-2 text-sm font-medium transition-colors`}
                              onClick={logout}
                            >
                              <LogoutIcon className={`w-5 h-5 mr-3 transition-colors ${active ? 'text-red-500' : 'text-slate-400'}`} aria-hidden="true" />
                              Logout
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </nav>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {isSettingsModalOpen && userCopy && (
          <motion.div 
            key="settings-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100]"
            onClick={() => setIsSettingsModalOpen(false)}
          >
            <div className="flex items-center justify-center h-full p-4">
              <motion.div 
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                className="w-full max-h-[90vh] flex flex-col bg-white dark:bg-slate-800 rounded-2xl text-left overflow-hidden shadow-2xl sm:max-w-2xl transform-gpu"
                onClick={(e) => e.stopPropagation()}
              >
            <div className="p-3 sm:p-6 overflow-y-auto">
                  <div className="w-full">
                    <div className="flex items-center mb-4 sm:mb-6">
                      <div className="flex flex-shrink-0 items-center justify-center h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-blue-100/50 dark:bg-blue-900/30">
                        <CogIcon className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                      </div>
                      <h3 className="text-lg sm:text-2xl font-extrabold tracking-tight dark:text-white text-slate-900 ml-2 sm:ml-4 block">Settings</h3>
                    </div>
                <div className="text-left w-full">
                  <div className="grid grid-cols-2 gap-2 sm:gap-5 mb-3 sm:mb-5">
                    <div>
                      <label htmlFor="firstName" className="block mb-1 sm:mb-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                            First name
                          </label>
                          <input
                            type="text"
                            id="firstName"
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                            value={userCopy.firstName}
                            required
                            onChange={(value) => {
                              userCopy.firstName = value.target.value;
                              setUserCopy({
                                ...userCopy,
                              });
                            }}
                          />
                        </div>
                    <div>
                      <label htmlFor="lastName" className="block mb-1 sm:mb-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                            Last name
                          </label>
                          <input
                            type="text"
                            id="lastName"
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                            value={userCopy.lastName}
                            required
                            onChange={(value) => {
                              userCopy.lastName = value.target.value;
                              setUserCopy({
                                ...userCopy,
                              });
                            }}
                          />
                        </div>
                      </div>
                      {!userCopy.advisor && (
                        <>
                      <div className="grid grid-cols-2 gap-2 sm:gap-5 mb-3 sm:mb-5">
                        <div>
                          <label htmlFor="cellNumber" className="block mb-1 sm:mb-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                                Cell number
                              </label>
                              <input
                                type="tel"
                                id="cellNumber"
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                                value={userCopy.cellNumber}
                                required
                                onChange={(value) => {
                                  userCopy.cellNumber = value.target.value;
                                  setUserCopy({
                                    ...userCopy,
                                  });
                                }}
                              />
                            </div>

                        <div>
                          <label htmlFor="birthday" className="block mb-1 sm:mb-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                                Birthday
                              </label>
                              <DatePicker
                                id="birthday"
                                showMonthDropdown
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={100}
                                maxDate={new Date()}
                                withPortal={isMobile}
                                onFocus={(e) => isMobile && e.target.blur()}
                                className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                                selected={userCopy.birthday ? new Date(userCopy.birthday + 'T00:00:00') : null}
                                required
                                onChange={(date: Date | null) => {
                                  if (date) {
                                    const y = date.getFullYear();
                                    const m = String(date.getMonth() + 1).padStart(2, '0');
                                    const d = String(date.getDate()).padStart(2, '0');
                                    userCopy.birthday = `${y}-${m}-${d}`;
                                  } else {
                                    userCopy.birthday = '';
                                  }
                                  setUserCopy({
                                    ...userCopy,
                                  });
                                }}
                              />
                            </div>
                          </div>
                          <div className="mb-3 sm:mb-5 lg:mb-6 lg:mr-10">
                            <label htmlFor="streetAddress" className="block mb-1 sm:mb-2 text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-300">
                              Street address
                            </label>
                            <input
                              type="text"
                              id="streetAddress"
                          className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                              value={userCopy.streetAddress}
                              required
                              onChange={(value) => {
                                userCopy.streetAddress = value.target.value;
                                setUserCopy({
                                  ...userCopy,
                                });
                              }}
                            />
                          </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-5 mb-3 sm:mb-5">
                        <div className="col-span-2 sm:col-span-1">
                          <label htmlFor="city" className="block mb-1 sm:mb-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                                City
                              </label>
                              <input
                                type="text"
                                id="city"
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                                value={userCopy.city}
                                required
                                onChange={(value) => {
                                  userCopy.city = value.target.value;
                                  setUserCopy({
                                    ...userCopy,
                                  });
                                }}
                              />
                            </div>
                        <div>
                          <label htmlFor="state" className="block mb-1 sm:mb-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                                State
                              </label>
                              <Listbox
                                value={userCopy.stateText}
                                onChange={(value) => {
                                  userCopy.stateText = value;
                                  setUserCopy({
                                    ...userCopy,
                                  });
                                }}
                              >
                                <div className="relative mt-1">
                              <Listbox.Button className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm text-left">
                                <span className="block truncate">{userCopy.stateText}</span>
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                      <SelectorIcon className="w-5 h-5 text-slate-400" aria-hidden="true" />
                                    </span>
                                  </Listbox.Button>
                                  <Transition
                                    as={Fragment}
                                    leave="transition ease-in duration-100"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                  >
                                <Listbox.Options className="absolute w-full py-1 mt-2 overflow-auto text-base bg-white dark:bg-slate-800 rounded-xl shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                                      {states.map((state, personIdx) => (
                                        <Listbox.Option
                                          key={personIdx}
                                          className={({ active }) =>
                                        `cursor-pointer select-none relative py-2 pl-10 pr-4 transition-colors ${
                                          active ? 'text-blue-900 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-100' : 'text-slate-900 dark:text-slate-200'
                                            }`
                                          }
                                          value={state}
                                        >
                                          {({ selected }) => (
                                            <>
                                          <span className={`block truncate ${selected ? 'font-bold' : 'font-medium'}`}>
                                                {state}
                                              </span>
                                              {selected ? (
                                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400">
                                                  <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                                </span>
                                              ) : null}
                                            </>
                                          )}
                                        </Listbox.Option>
                                      ))}
                                    </Listbox.Options>
                                  </Transition>
                                </div>
                              </Listbox>
                            </div>
                        <div>
                          <label htmlFor="zipCode" className="block mb-1 sm:mb-2 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                                Zip code
                              </label>
                              <input
                                type="text"
                                id="zipCode"
                            className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm"
                                value={userCopy.zipCode}
                                required
                                onChange={(value) => {
                                  userCopy.zipCode = value.target.value;
                                  setUserCopy({
                                    ...userCopy,
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </>
                      )}
                  <div className="mt-4 sm:mt-6 mb-1 sm:mb-2">
                    <label htmlFor="terms" className="inline-flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          id="terms"
                          type="checkbox"
                          className="sr-only peer"
                          checked={userCopy.darkMode}
                          onChange={(value) => {
                            userCopy.darkMode = value.target.checked;
                            setUserCopy({
                              ...userCopy,
                            });
                          }}
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                      </div>
                      <span className="ml-3 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Dark mode enabled
                      </span>
                    </label>
                      </div>
                    </div>
                  </div>
                </div>
            <div className="bg-slate-50 dark:bg-slate-900/50 px-3 sm:px-6 py-3 sm:py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 shrink-0">
              <button
                type="button"
                className="inline-flex justify-center rounded-xl border border-slate-300 dark:border-slate-600 px-4 py-2 sm:px-6 sm:py-2.5 bg-white dark:bg-slate-800 text-base font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm sm:text-sm"
                onClick={() => setIsSettingsModalOpen(false)}
              >
                Cancel
              </button>
                  <button
                    type="button"
                className="inline-flex justify-center rounded-xl px-4 py-2 sm:px-6 sm:py-2.5 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 sm:text-sm"
                    onClick={submit}
                  >
                Save Changes
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
